<template>
  <div class="alice-cl">
    <header class="alice-cl__head">
      <div class="alice-cl__head-main">
        <div class="alice-cl__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Clients</span>
        </div>
        <h1 class="alice-cl__title">
          Clients
          <span class="alice-cl__count">{{ clients.length }}</span>
        </h1>
        <p class="alice-cl__subtitle">
          Manage clients and see how much of their data lives in the vector
          database. Each client's data is isolated — their custom ChatGPT only
          sees their own.
        </p>
      </div>
      <div class="alice-cl__head-actions">
        <button class="alice-cl__btn" type="button" @click="openCreate">
          <Icon name="plus" :size="14" />
          <span>New client</span>
        </button>
      </div>
    </header>

    <div v-if="error" class="alice-cl__state alice-cl__state--error">
      {{ error }}
    </div>

    <div class="alice-cl__body">
      <!-- Maintenance -->
      <section class="alice-cl__maint">
        <div class="alice-cl__maint-text">
          <strong>Maintenance</strong>
          <span>
            Backfill links existing projects to clients by name. Re-tag stamps
            client_id onto existing vector points (no re-embedding).
          </span>
        </div>
        <div class="alice-cl__maint-actions">
          <button
            class="alice-cl__btn"
            type="button"
            :disabled="busy"
            @click="openReconcile"
          >
            Reconcile names
          </button>
          <button
            class="alice-cl__btn alice-cl__btn--ghost"
            type="button"
            :disabled="busy"
            @click="runBackfill"
          >
            {{ backfilling ? "Backfilling…" : "Backfill clients" }}
          </button>
          <button
            class="alice-cl__btn alice-cl__btn--ghost"
            type="button"
            :disabled="busy"
            @click="runRetag"
          >
            {{ retagging ? "Re-tagging…" : "Re-tag vector points" }}
          </button>
          <button
            class="alice-cl__btn alice-cl__btn--ghost"
            type="button"
            :disabled="busy"
            @click="runDedupe"
          >
            {{ deduping ? "De-duplicating…" : "De-duplicate moments" }}
          </button>
          <button
            class="alice-cl__btn alice-cl__btn--ghost"
            type="button"
            :disabled="busy"
            @click="fetchStats"
          >
            {{ loadingStats ? "Refreshing…" : "Refresh counts" }}
          </button>
        </div>
      </section>

      <p v-if="maintMsg" class="alice-cl__maint-msg">{{ maintMsg }}</p>

      <!-- Table -->
      <table class="alice-cl__table">
        <thead>
          <tr>
            <th>Client</th>
            <th>ID</th>
            <th class="alice-cl__th-right">Projects</th>
            <th
              v-for="c in COLLECTIONS"
              :key="c.key"
              class="alice-cl__th-right"
            >
              {{ c.label }}
            </th>
            <th class="alice-cl__th-right">Total</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows" :key="row.client_id">
            <tr class="alice-cl__row" @click="toggle(row.client_id)">
              <td class="alice-cl__cell-name">
                <span
                  class="alice-cl__swatch"
                  :style="{ background: row.color || 'var(--vp-grijs-120)' }"
                />
                {{ row.name || "Untitled client" }}
              </td>
              <td class="alice-cl__cell-id">{{ row.client_id }}</td>
              <td class="alice-cl__cell-num">
                {{ projectsForClient(row.client_id).length }}
              </td>
              <td
                v-for="c in COLLECTIONS"
                :key="c.key"
                class="alice-cl__cell-num"
              >
                {{ fmt(row.counts && row.counts[c.key]) }}
              </td>
              <td class="alice-cl__cell-num alice-cl__cell-total">
                {{ fmt(row.total) }}
              </td>
              <td class="alice-cl__cell-actions">
                <Icon
                  :name="
                    expanded === row.client_id
                      ? 'chevron-down'
                      : 'chevron-right'
                  "
                  :size="14"
                />
              </td>
            </tr>
            <tr v-if="expanded === row.client_id" class="alice-cl__expand">
              <td
                :colspan="COLLECTIONS.length + 4"
                class="alice-cl__expand-cell"
              >
                <div class="alice-cl__expand-grid">
                  <!-- Projects -->
                  <div class="alice-cl__expand-block">
                    <div class="alice-cl__expand-label">
                      Projects ({{ projectsForClient(row.client_id).length }})
                    </div>
                    <ul class="alice-cl__list">
                      <li
                        v-for="p in projectsForClient(row.client_id)"
                        :key="p.uid"
                      >
                        {{ p.title || "Untitled" }}
                      </li>
                      <li
                        v-if="!projectsForClient(row.client_id).length"
                        class="alice-cl__muted"
                      >
                        No projects assigned.
                      </li>
                    </ul>
                  </div>

                  <!-- API keys -->
                  <div class="alice-cl__expand-block">
                    <div class="alice-cl__expand-label">
                      API keys (ChatGPT access)
                    </div>
                    <ul class="alice-cl__list">
                      <li
                        v-for="k in keysForClient(row.client_id)"
                        :key="k.key_id"
                        class="alice-cl__key"
                      >
                        <div class="alice-cl__key-main">
                          <span :class="{ 'alice-cl__muted': k.revoked }">
                            {{ k.label }}
                            <em v-if="k.revoked">(revoked)</em>
                          </span>
                          <code
                            v-if="k.api_key"
                            class="alice-cl__key-inline"
                            :class="{ 'alice-cl__muted': k.revoked }"
                          >
                            {{
                              revealed[k.key_id]
                                ? k.api_key
                                : maskKey(k.api_key)
                            }}
                          </code>
                          <span
                            v-else
                            class="alice-cl__muted alice-cl__key-inline"
                          >
                            (legacy key — reissue to view)
                          </span>
                        </div>
                        <div class="alice-cl__key-actions">
                          <button
                            v-if="k.api_key"
                            class="alice-cl__link-btn"
                            type="button"
                            @click="toggleReveal(k.key_id)"
                          >
                            {{ revealed[k.key_id] ? "Hide" : "Show" }}
                          </button>
                          <button
                            v-if="k.api_key"
                            class="alice-cl__link-btn"
                            type="button"
                            @click="copyText(k.api_key)"
                          >
                            Copy
                          </button>
                          <button
                            v-if="!k.revoked"
                            class="alice-cl__link-btn alice-cl__link-btn--danger"
                            type="button"
                            @click="revokeKey(k)"
                          >
                            Revoke
                          </button>
                        </div>
                      </li>
                      <li
                        v-if="!keysForClient(row.client_id).length"
                        class="alice-cl__muted"
                      >
                        No keys issued.
                      </li>
                    </ul>
                    <button
                      class="alice-cl__btn alice-cl__btn--ghost alice-cl__btn--sm"
                      type="button"
                      :disabled="busy"
                      @click="issueKey(row)"
                    >
                      <Icon name="plus" :size="12" />
                      <span>Issue API key</span>
                    </button>
                  </div>

                  <!-- Manage -->
                  <div class="alice-cl__expand-block">
                    <div class="alice-cl__expand-label">Manage</div>
                    <div class="alice-cl__manage-actions">
                      <button
                        class="alice-cl__btn alice-cl__btn--ghost alice-cl__btn--sm"
                        type="button"
                        @click="openEdit(row)"
                      >
                        Edit
                      </button>
                      <button
                        class="alice-cl__btn alice-cl__btn--ghost alice-cl__btn--sm alice-cl__btn--danger"
                        type="button"
                        @click="deleteClient(row)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>

          <!-- Unassigned -->
          <tr
            v-if="unassigned && unassigned.total"
            class="alice-cl__row alice-cl__row--unassigned"
          >
            <td class="alice-cl__cell-name">
              <span class="alice-cl__swatch alice-cl__swatch--empty" />
              Unassigned
            </td>
            <td class="alice-cl__cell-id">—</td>
            <td class="alice-cl__cell-num">—</td>
            <td
              v-for="c in COLLECTIONS"
              :key="c.key"
              class="alice-cl__cell-num"
            >
              {{ fmt(unassigned[c.key]) }}
            </td>
            <td class="alice-cl__cell-num alice-cl__cell-total">
              {{ fmt(unassigned.total) }}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
      <p v-if="!rows.length" class="alice-cl__empty">
        No clients yet. Create one, or run Backfill to import them from existing
        projects.
      </p>

      <!-- Team MCP connections: colleagues' OAuth sessions -->
      <section class="alice-cl__sessions">
        <div class="alice-cl__maint">
          <div class="alice-cl__maint-text">
            <strong>Team MCP connections</strong>
            <span>
              Colleagues signed in to the research database from ChatGPT or
              Claude. A session ends {{ sessionLifetimeDays }} days after
              sign-in and is re-checked against Google on every refresh.
              Revoking a colleague also revokes their Google grant.
            </span>
          </div>
          <div class="alice-cl__maint-actions">
            <button
              class="alice-cl__btn alice-cl__btn--ghost"
              type="button"
              :disabled="sessionsBusy"
              @click="purgeStaleSessions"
            >
              Purge stale
            </button>
            <button
              class="alice-cl__btn alice-cl__btn--ghost"
              type="button"
              :disabled="sessionsBusy"
              @click="loadSessions"
            >
              {{ sessionsLoading ? "Refreshing…" : "Refresh" }}
            </button>
          </div>
        </div>
        <p v-if="sessionsMsg" class="alice-cl__maint-msg">{{ sessionsMsg }}</p>
        <table v-if="sessions.length" class="alice-cl__table">
          <thead>
            <tr>
              <th>Colleague</th>
              <th>Client</th>
              <th>Signed in</th>
              <th>Last refresh</th>
              <th>Expires</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in sessions"
              :key="s.session_id"
              class="alice-cl__row alice-cl__row--static"
            >
              <td class="alice-cl__cell-name">{{ s.email || "—" }}</td>
              <td>{{ s.client_name || s.client_id || "—" }}</td>
              <td>{{ fmtDate(s.auth_time) }}</td>
              <td>{{ fmtDate(s.last_refreshed_at) }}</td>
              <td>{{ fmtDate(s.expires_at) }}</td>
              <td :class="{ 'alice-cl__muted': s.status !== 'active' }">
                {{ s.status }}
              </td>
              <td class="alice-cl__cell-actions alice-cl__session-actions">
                <button
                  class="alice-cl__link-btn alice-cl__link-btn--danger"
                  type="button"
                  :disabled="sessionsBusy"
                  @click="revokeSession(s)"
                >
                  Revoke
                </button>
                <button
                  class="alice-cl__link-btn alice-cl__link-btn--danger"
                  type="button"
                  :disabled="sessionsBusy"
                  @click="revokeColleague(s.email)"
                >
                  Revoke all
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else-if="!sessionsLoading" class="alice-cl__empty">
          No team connections.
        </p>
      </section>
    </div>

    <!-- Create / edit dialog -->
    <v-dialog v-model="dialog" max-width="420">
      <v-card class="alice-cl__dialog">
        <div class="alice-cl__dialog-title">
          {{ editing ? "Edit client" : "New client" }}
        </div>
        <v-text-field
          v-model="form.name"
          placeholder="Client name"
          variant="outlined"
          autofocus
          hide-details
          class="alice-cl__dialog-input"
        />
        <v-text-field
          v-model="form.color"
          placeholder="Color (hex, optional) e.g. #1f6feb"
          variant="outlined"
          hide-details
          class="alice-cl__dialog-input"
        />
        <div class="alice-cl__dialog-actions">
          <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="black"
            :loading="saving"
            :disabled="!form.name"
            @click="saveClient"
            >{{ editing ? "Save" : "Create" }}</v-btn
          >
        </div>
      </v-card>
    </v-dialog>

    <!-- Raw key reveal -->
    <v-dialog v-model="keyDialog" max-width="520">
      <v-card class="alice-cl__dialog">
        <div class="alice-cl__dialog-title">API key created</div>
        <p class="alice-cl__dialog-text">
          Hand this key to the client for their ChatGPT connection. You can view
          it again any time from this client's row.
        </p>
        <code class="alice-cl__key-value">{{ revealedKey }}</code>
        <div class="alice-cl__dialog-actions">
          <v-btn variant="text" @click="copyText(revealedKey)">Copy</v-btn>
          <v-btn color="black" @click="keyDialog = false">Done</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Reconcile client names -->
    <v-dialog v-model="reconcileDialog" max-width="720">
      <v-card class="alice-cl__dialog">
        <div class="alice-cl__dialog-title">Reconcile client names</div>
        <p class="alice-cl__dialog-text">
          Every distinct client name found on projects. Map each spelling to a
          canonical client (or create one) — all matching projects get that
          client_id and the canonical name. Different spellings of the same
          client should point at one client.
        </p>

        <div v-if="reconcileLoading" class="alice-cl__dialog-text">
          Loading…
        </div>
        <table v-else class="alice-cl__rec-table">
          <thead>
            <tr>
              <th>Name on projects</th>
              <th class="alice-cl__th-right">Projects</th>
              <th>Map to client</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in reconcileNames" :key="row.name || '(blank)'">
              <td>
                <span v-if="row.name">{{ row.name }}</span>
                <em v-else class="alice-cl__muted">(no name)</em>
                <span v-if="row.linked === row.count" class="alice-cl__rec-ok">
                  linked
                </span>
              </td>
              <td class="alice-cl__cell-num">{{ row.count }}</td>
              <td>
                <select
                  v-model="reconcileChoice[row.name]"
                  class="alice-cl__rec-select"
                >
                  <option v-if="row.name" value="__new__">
                    ✛ Create “{{ row.name }}”
                  </option>
                  <option v-for="c in clients" :key="c.uid" :value="c.uid">
                    {{ c.name }}
                  </option>
                </select>
              </td>
              <td>
                <button
                  class="alice-cl__btn alice-cl__btn--ghost alice-cl__btn--sm"
                  type="button"
                  :disabled="
                    reconcileBusy[row.name] || !reconcileChoice[row.name]
                  "
                  @click="applyReconcile(row)"
                >
                  {{ reconcileBusy[row.name] ? "Applying…" : "Apply" }}
                </button>
              </td>
            </tr>
            <tr v-if="!reconcileNames.length">
              <td colspan="4" class="alice-cl__muted">
                No client names found on projects.
              </td>
            </tr>
          </tbody>
        </table>

        <div class="alice-cl__dialog-actions">
          <v-btn color="black" @click="reconcileDialog = false">Done</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "@/firebase";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import Icon from "@/components/alice/Icon.vue";

const COLLECTIONS = [
  { key: "projects", label: "Projects (v)" },
  { key: "moments", label: "Moments" },
  { key: "insights", label: "Reports" },
  { key: "proposals", label: "Proposals" },
  { key: "test_scripts", label: "Test scripts" },
  // How much historical-archive material got linked to this client. The link is
  // derived from a case-insensitive name match during ingest, so this count is
  // what you check before switching Archive access on for a client.
  { key: "archive_chunks", label: "Archive" },
];

export default {
  name: "AdminClients",
  components: { Icon },
  data() {
    return {
      COLLECTIONS,
      stats: null,
      keysByClient: {},
      error: null,
      loadingStats: false,
      backfilling: false,
      retagging: false,
      deduping: false,
      maintMsg: "",
      expanded: null,
      dialog: false,
      editing: null,
      saving: false,
      form: { name: "", color: "" },
      keyDialog: false,
      revealedKey: "",
      revealed: {},
      reconcileDialog: false,
      reconcileLoading: false,
      reconcileNames: [],
      reconcileChoice: {},
      reconcileBusy: {},
      sessions: [],
      sessionsLoading: false,
      sessionsBusy: false,
      sessionsMsg: "",
      sessionLifetimeDays: 30,
    };
  },
  computed: {
    ...mapState({
      clients: (s) => s.clients,
      projects: (s) => s.projects,
    }),
    busy() {
      return (
        this.backfilling || this.retagging || this.deduping || this.loadingStats
      );
    },
    // Merge the clients collection with vector stats so every client shows,
    // even those with zero data.
    rows() {
      const statMap = {};
      (this.stats?.clients || []).forEach((c) => {
        statMap[c.client_id] = c;
      });
      return this.clients.map((c) => {
        const s = statMap[c.uid] || {};
        return {
          client_id: c.uid,
          name: c.name,
          color: c.color,
          counts: s.counts || {},
          total: s.total || 0,
        };
      });
    },
    unassigned() {
      return this.stats?.unassigned || null;
    },
  },
  async mounted() {
    if (this.clients.length === 0) await this.$store.dispatch("fetchClients");
    if (this.projects.length === 0) this.$store.dispatch("fetchProjects");
    await this.fetchStats();
    this.loadSessions();
  },
  methods: {
    fmt(n) {
      if (n == null) return "—";
      return Number(n).toLocaleString("en-GB");
    },
    fmtDate(ms) {
      if (!ms) return "—";
      return new Date(ms).toLocaleString("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    },
    async loadSessions() {
      this.sessionsLoading = true;
      try {
        const fn = httpsCallable(functions, "listMcpTeamSessions");
        const res = await fn();
        this.sessions = res.data?.sessions || [];
        if (res.data?.lifetime_days) {
          this.sessionLifetimeDays = res.data.lifetime_days;
        }
      } catch (e) {
        // Non-admins get permission-denied; the stats card already tells them
        // they have no access, so stay quiet here.
        if (e?.code !== "functions/permission-denied") {
          console.error("loadSessions failed", e);
          this.sessionsMsg = `Failed to load team connections: ${
            e.message || e
          }`;
        }
      } finally {
        this.sessionsLoading = false;
      }
    },
    async revokeSessions(payload, label) {
      if (!confirm(`Revoke ${label}?`)) return;
      this.sessionsBusy = true;
      this.sessionsMsg = "";
      try {
        const fn = httpsCallable(functions, "revokeMcpTeamSessions");
        const res = await fn(payload);
        this.sessionsMsg = `Revoked ${res.data?.revoked ?? 0} session(s).`;
        await this.loadSessions();
      } catch (e) {
        console.error("revokeSessions failed", e);
        this.sessionsMsg = `Revoke failed: ${e.message || e}`;
      } finally {
        this.sessionsBusy = false;
      }
    },
    revokeSession(s) {
      return this.revokeSessions(
        { session_id: s.session_id },
        `the ${s.client_name || "MCP"} connection of ${s.email}`
      );
    },
    revokeColleague(email) {
      if (!email) return;
      return this.revokeSessions(
        { email },
        `ALL MCP connections of ${email} (this also revokes their Google grant)`
      );
    },
    purgeStaleSessions() {
      return this.revokeSessions(
        { purge_stale: true },
        "every expired or legacy session"
      );
    },
    projectsForClient(clientId) {
      return this.projects.filter((p) => p.client_id === clientId);
    },
    keysForClient(clientId) {
      return this.keysByClient[clientId] || [];
    },
    maskKey(key) {
      if (!key) return "";
      const tail = key.slice(-4);
      return `${key.slice(0, 4)}••••••••${tail}`;
    },
    toggleReveal(keyId) {
      this.revealed = { ...this.revealed, [keyId]: !this.revealed[keyId] };
    },
    toggle(id) {
      this.expanded = this.expanded === id ? null : id;
      if (this.expanded && !this.keysByClient[id]) this.loadKeys(id);
    },
    async fetchStats() {
      this.loadingStats = true;
      this.error = null;
      try {
        const fn = httpsCallable(functions, "getClientVectorStats");
        const res = await fn();
        if (res.data && res.data.error === 403) {
          this.error = "You don't have access to client administration.";
          return;
        }
        this.stats = res.data;
      } catch (e) {
        console.error(e);
        this.error = `Failed to load client stats: ${e.message || e}`;
      } finally {
        this.loadingStats = false;
      }
    },
    async loadKeys(clientId) {
      try {
        const fn = httpsCallable(functions, "listMcpClientKeys");
        const res = await fn({ client_id: clientId });
        this.keysByClient = {
          ...this.keysByClient,
          [clientId]: res.data?.keys || [],
        };
      } catch (e) {
        console.error("loadKeys failed", e);
      }
    },
    openCreate() {
      this.editing = null;
      this.form = { name: "", color: "" };
      this.dialog = true;
    },
    openEdit(row) {
      this.editing = row.client_id;
      this.form = { name: row.name || "", color: row.color || "" };
      this.dialog = true;
    },
    async saveClient() {
      if (!this.form.name) return;
      this.saving = true;
      try {
        if (this.editing) {
          await updateDoc(doc(db, "clients", this.editing), {
            name: this.form.name.trim(),
            color: this.form.color.trim() || null,
          });
        } else {
          await this.$store.dispatch("createClient", {
            name: this.form.name,
            color: this.form.color.trim() || null,
          });
        }
        await this.$store.dispatch("fetchClients");
        this.dialog = false;
      } catch (e) {
        console.error("saveClient failed", e);
        this.error = `Failed to save client: ${e.message || e}`;
      } finally {
        this.saving = false;
      }
    },
    async deleteClient(row) {
      const assigned = this.projectsForClient(row.client_id);
      if (assigned.length) {
        alert(
          `Cannot delete "${row.name}" — ${assigned.length} project(s) still reference it. Reassign them first.`
        );
        return;
      }
      if (!confirm(`Delete client "${row.name}"?`)) return;
      try {
        await deleteDoc(doc(db, "clients", row.client_id));
        await this.$store.dispatch("fetchClients");
      } catch (e) {
        console.error("deleteClient failed", e);
      }
    },
    async runBackfill() {
      this.backfilling = true;
      this.maintMsg = "";
      try {
        const fn = httpsCallable(functions, "backfillProjectClients");
        const res = await fn();
        if (res.data?.error === 403) {
          this.maintMsg = "Access denied.";
          return;
        }
        this.maintMsg = `Backfill done — ${res.data.clientsCreated} clients created, ${res.data.projectsUpdated} projects linked.`;
        await this.$store.dispatch("fetchClients");
        await this.$store.dispatch("fetchProjects");
        await this.fetchStats();
      } catch (e) {
        console.error(e);
        this.maintMsg = `Backfill failed: ${e.message || e}`;
      } finally {
        this.backfilling = false;
      }
    },
    async runRetag() {
      this.retagging = true;
      this.maintMsg = "";
      try {
        const fn = httpsCallable(functions, "setClientIdOnExistingPoints", {
          timeout: 540000,
        });
        const res = await fn();
        if (res.data?.error === 403) {
          this.maintMsg = "Access denied.";
          return;
        }
        this.maintMsg = `Re-tag done — ${res.data.projectsProcessed} projects processed (${res.data.projectsSkipped} skipped without a client).`;
        await this.fetchStats();
      } catch (e) {
        console.error(e);
        this.maintMsg = `Re-tag failed: ${e.message || e}`;
      } finally {
        this.retagging = false;
      }
    },
    // Removes the duplicate moment points left behind while point ids were
    // random. Safe to re-run: a second pass finds nothing to delete.
    async runDedupe() {
      this.deduping = true;
      this.maintMsg = "";
      try {
        const fn = httpsCallable(functions, "dedupeMomentPoints", {
          timeout: 540000,
        });
        const res = await fn();
        if (res.data?.error === 403) {
          this.maintMsg = "Access denied.";
          return;
        }
        const d = res.data;
        this.maintMsg =
          `De-duplicate done — ${d.deleted_points} duplicate point(s) removed ` +
          `across ${d.duplicated_moments} moment(s); ${d.distinct_moments} distinct ` +
          `moments scanned.` +
          (d.orphan_groups_removed
            ? ` ${d.orphan_groups_removed} orphaned group(s) dropped.`
            : "") +
          (d.repointed_docs ? ` ${d.repointed_docs} doc(s) repointed.` : "") +
          (d.complete ? "" : " Stopped at the time budget — run again.");
        await this.fetchStats();
      } catch (e) {
        console.error(e);
        this.maintMsg = `De-duplicate failed: ${e.message || e}`;
      } finally {
        this.deduping = false;
      }
    },
    async issueKey(row) {
      try {
        const fn = httpsCallable(functions, "createMcpClientKey");
        const res = await fn({ client_id: row.client_id });
        this.revealedKey = res.data.api_key;
        this.keyDialog = true;
        await this.loadKeys(row.client_id);
      } catch (e) {
        console.error("issueKey failed", e);
        this.error = `Failed to issue key: ${e.message || e}`;
      }
    },
    async revokeKey(key) {
      if (!confirm(`Revoke key "${key.label}"? The client will lose access.`))
        return;
      try {
        const fn = httpsCallable(functions, "revokeMcpClientKey");
        await fn({ key_id: key.key_id });
        await this.loadKeys(key.client_id);
      } catch (e) {
        console.error("revokeKey failed", e);
      }
    },
    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        /* clipboard unavailable */
      }
    },
    openReconcile() {
      this.reconcileDialog = true;
      this.loadReconcile();
    },
    async loadReconcile() {
      this.reconcileLoading = true;
      try {
        const fn = httpsCallable(functions, "getProjectClientNames");
        const res = await fn();
        if (res.data?.error === 403) {
          this.error = "Access denied.";
          return;
        }
        this.reconcileNames = res.data.names || [];
        // Default each row to its existing link, a same-name client, or "create new".
        const choice = {};
        this.reconcileNames.forEach((n) => {
          const match = this.clients.find(
            (c) => (c.name || "").toLowerCase() === (n.name || "").toLowerCase()
          );
          if (n.client_ids.length === 1) choice[n.name] = n.client_ids[0];
          else if (match) choice[n.name] = match.uid;
          else if (n.name) choice[n.name] = "__new__";
          else choice[n.name] = "";
        });
        this.reconcileChoice = choice;
      } catch (e) {
        console.error("loadReconcile failed", e);
        this.error = `Failed to load names: ${e.message || e}`;
      } finally {
        this.reconcileLoading = false;
      }
    },
    async applyReconcile(row) {
      const choice = this.reconcileChoice[row.name];
      if (!choice) return;
      this.reconcileBusy = { ...this.reconcileBusy, [row.name]: true };
      try {
        let clientId = choice;
        if (choice === "__new__") {
          clientId = await this.$store.dispatch("createClient", {
            name: row.name,
          });
        }
        const fn = httpsCallable(functions, "assignClientToProjectsByName");
        await fn({ name: row.name, client_id: clientId });
        await this.$store.dispatch("fetchClients");
        await this.$store.dispatch("fetchProjects");
        await this.loadReconcile();
        await this.fetchStats();
      } catch (e) {
        console.error("applyReconcile failed", e);
        this.error = `Failed to apply: ${e.message || e}`;
      } finally {
        this.reconcileBusy = { ...this.reconcileBusy, [row.name]: false };
      }
    },
  },
};
</script>

<style scoped>
.alice-cl {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px 64px;
  font-family: var(--font-body);
  color: var(--fg-1);
}
.alice-cl__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
}
.alice-cl__crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-cl__title {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vp-black);
}
.alice-cl__count {
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 600;
  color: var(--fg-3);
  margin-left: 6px;
}
.alice-cl__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--fg-3);
  max-width: 60ch;
  line-height: 1.5;
}
.alice-cl__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity var(--dur-fast) var(--ease-hover);
}
.alice-cl__btn:hover {
  opacity: 0.85;
}
.alice-cl__btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-cl__btn--ghost {
  background: var(--vp-white);
  color: var(--vp-black);
  border-color: var(--border-hairline);
}
.alice-cl__btn--sm {
  padding: 6px 10px;
  font-size: 12px;
}
.alice-cl__btn--danger {
  color: var(--vp-rood-100, #c0392b);
}
.alice-cl__state {
  padding: 16px;
  font-size: 14px;
}
.alice-cl__state--error {
  color: var(--vp-rood-100, #c0392b);
}
.alice-cl__maint {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: var(--bg-muted);
  border: 1px solid var(--border-hairline);
  margin-bottom: 8px;
}
.alice-cl__maint-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: var(--fg-3);
}
.alice-cl__maint-text strong {
  color: var(--vp-black);
  font-size: 13px;
}
.alice-cl__maint-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.alice-cl__maint-msg {
  font-size: 12px;
  color: var(--fg-2);
  margin: 0 0 16px;
}
.alice-cl__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.alice-cl__table th {
  text-align: left;
  padding: 10px 12px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-cl__th-right {
  text-align: right;
}
.alice-cl__row {
  cursor: pointer;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-cl__row:hover {
  background: var(--bg-muted);
}
.alice-cl__row--unassigned {
  cursor: default;
  color: var(--fg-3);
}
.alice-cl__row--unassigned:hover {
  background: transparent;
}
.alice-cl__table td {
  padding: 10px 12px;
}
.alice-cl__cell-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--vp-black);
}
.alice-cl__swatch {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.alice-cl__swatch--empty {
  border: 1px dashed var(--fg-3);
  background: transparent !important;
}
.alice-cl__cell-id {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--fg-3);
}
.alice-cl__cell-num {
  text-align: right;
  font-family: var(--font-mono);
}
.alice-cl__cell-total {
  font-weight: 700;
  color: var(--vp-black);
}
.alice-cl__cell-actions {
  text-align: right;
  color: var(--fg-3);
}
.alice-cl__expand-cell {
  background: var(--bg-muted);
  padding: 16px 12px;
}
.alice-cl__expand-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.alice-cl__expand-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-cl__list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
  font-size: 13px;
}
.alice-cl__list li {
  padding: 3px 0;
}
.alice-cl__key {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}
.alice-cl__key-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.alice-cl__key-inline {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--fg-2);
  word-break: break-all;
}
.alice-cl__key-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}
.alice-cl__muted {
  color: var(--fg-3);
}
.alice-cl__link-btn {
  background: none;
  border: none;
  color: var(--vp-blauw-100, #1f6feb);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.alice-cl__link-btn--danger {
  color: var(--vp-rood-100, #c0392b);
}
.alice-cl__manage-actions {
  display: flex;
  gap: 8px;
}
.alice-cl__empty {
  padding: 24px 12px;
  color: var(--fg-3);
  font-size: 13px;
}
.alice-cl__dialog {
  padding: 24px;
  border-radius: 0 !important;
  font-family: var(--font-body);
}
.alice-cl__dialog-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--vp-black);
}
.alice-cl__dialog-text {
  font-size: 13px;
  color: var(--fg-2);
  margin-bottom: 12px;
  line-height: 1.5;
}
.alice-cl__dialog-input {
  margin-bottom: 16px;
}
.alice-cl__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.alice-cl__key-value {
  display: block;
  padding: 12px;
  background: var(--bg-muted);
  border: 1px solid var(--border-hairline);
  font-family: var(--font-mono);
  font-size: 12px;
  word-break: break-all;
  margin-bottom: 16px;
}
.alice-cl__rec-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 16px;
}
.alice-cl__rec-table th {
  text-align: left;
  padding: 8px 10px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-cl__rec-table td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-hairline);
  vertical-align: middle;
}
.alice-cl__rec-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  cursor: pointer;
}
.alice-cl__rec-ok {
  margin-left: 8px;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vp-turquoise-100, #1aa);
}

.alice-cl__sessions {
  margin-top: 40px;
}
.alice-cl__row--static {
  cursor: default;
}
.alice-cl__session-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
